import { useQuery } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { supabaseMarketData, isMarketDataConfigured } from '@/lib/supabase-market-data'
import { captureError } from '@/lib/sentry'

const PROTOCOL_STATS_URL = 'https://vkhijzofpanjuolcdazv.supabase.co/functions/v1/protocol-stats'
const SOL_PRICE_URL = 'https://hermes.pyth.network/api/latest_price_feeds?ids[]=0xef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d'

export interface ProtocolStatsResponse {
  tokensLaunched: number
  totalVolume: number
  activeUsers: number
  creatorEarningsLamports: number
  creatorEarningsUsd: number
  volume?: { dbcUsd: number; dammV2Usd: number; totalUsd: number }
  creatorEarnings?: { dbcLamports: number; dbcUsd: number; dammV2Usd: number; totalUsd: number }
  launches?: { count: number; graduatedCount: number }
  dammV2SyncedAt?: string | null
}

export interface PerPoolReconciliation {
  symbol: string
  mintAddress: string
  dammPoolAddress: string
  onchainVolumeUsd: number      // damm_v2_cumulative_volume_usd_onchain
  dbVolumeSol: number           // SUM(quote_amount) from market-data trades for this DAMM V2 pool
  dbVolumeUsd: number           // dbVolumeSol × current SOL price (approximation; see note)
  captureRate: number           // dbVolumeUsd / onchainVolumeUsd; expect ~1.0
  status: 'ok' | 'warning' | 'error'
  syncedAt: string | null
  baseDecimals: number | null
  totalLpBFeeLamports: number
}

export interface GlobalMetricsHealth {
  stats: ProtocolStatsResponse
  perPool: PerPoolReconciliation[]
  solPriceUsd: number
  fetchedAt: number
}

async function fetchSolPriceUsd(): Promise<number> {
  const r = await fetch(SOL_PRICE_URL)
  const j = await r.json()
  const p = j[0].price
  return Number(p.price) * Math.pow(10, p.expo)
}

async function fetchProtocolStats(): Promise<ProtocolStatsResponse> {
  // protocol-stats edge fn enforces verify_jwt; use the publishable anon key.
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY
  const r = await fetch(PROTOCOL_STATS_URL, {
    headers: { Authorization: `Bearer ${anon}` },
  })
  if (!r.ok) throw new Error(`protocol-stats HTTP ${r.status}`)
  return r.json()
}

export function useGlobalMetricsHealth() {
  return useQuery<GlobalMetricsHealth>({
    queryKey: ['global-metrics-health'],
    queryFn: async () => {
      const [stats, solPriceUsd] = await Promise.all([fetchProtocolStats(), fetchSolPriceUsd()])

      // Per-pool reconciliation needs the launch-side pool_stats values +
      // market-data trades aggregation. PostgREST embedded resource select
      // (`pool_stats:pool_address (...)`) requires an FK between
      // launched_tokens.pool_address and pool_stats.pool_address; no such FK
      // exists (would need orphan cleanup + UNIQUE index + FK creation —
      // separate structural workstream). Two-pass JS join sidesteps that.
      const { data: launchPools, error: launchErr } = await supabase
        .from('launched_tokens')
        .select('mint_address, symbol, damm_pool_address, pool_address')
        .not('damm_pool_address', 'is', null)
        .order('created_at', { ascending: true })
      if (launchErr) throw launchErr

      type PoolStatsRow = {
        pool_address: string
        damm_v2_cumulative_volume_usd_onchain: number | null
        damm_v2_cumulative_fee_usd: number | null
        damm_v2_synced_at: string | null
        damm_v2_base_decimals: number | null
        damm_v2_creator_fee_b_side_lamports: number | null
      }

      const launchPoolAddrs = (launchPools ?? [])
        .map((p) => p.pool_address)
        .filter((a): a is string => Boolean(a))
      const poolStatsByAddr = new Map<string, PoolStatsRow>()
      if (launchPoolAddrs.length > 0) {
        const { data: poolStats, error: psErr } = await supabase
          .from('pool_stats')
          .select('pool_address, damm_v2_cumulative_volume_usd_onchain, damm_v2_cumulative_fee_usd, damm_v2_synced_at, damm_v2_base_decimals, damm_v2_creator_fee_b_side_lamports')
          .in('pool_address', launchPoolAddrs)
        if (psErr) throw psErr
        for (const row of (poolStats ?? []) as PoolStatsRow[]) {
          poolStatsByAddr.set(row.pool_address, row)
        }
      }

      const dammPoolAddresses = (launchPools ?? [])
        .map((p) => p.damm_pool_address)
        .filter((s): s is string => Boolean(s))

      // Server-side aggregation via RPC. A prior `.from('trades').select(...).in(...)`
      // implementation hit the PostgREST 1000-row default once total trades across
      // requested pools exceeded 1000, dropping tail-sorted pool_addresses entirely
      // and falsely flagging them as reconciliation drift.
      const dbVolumesByPool = new Map<string, number>()
      if (isMarketDataConfigured() && supabaseMarketData && dammPoolAddresses.length > 0) {
        try {
          const { data: rows, error: rpcErr } = await supabaseMarketData
            .rpc('pool_aggregates_lifetime', { p_pool_addresses: dammPoolAddresses })
          if (rpcErr) throw rpcErr
          for (const r of (rows ?? []) as Array<{ pool_address: string; volume_sol: number | string }>) {
            dbVolumesByPool.set(r.pool_address, Number(r.volume_sol))
          }
        } catch (e) {
          console.warn('[useGlobalMetricsHealth] market-data pool_aggregates_lifetime RPC failed', e)
          captureError(e as Error, { feature: 'global-metrics' })
        }
      }

      const perPool: PerPoolReconciliation[] = (launchPools ?? []).map((p) => {
        const ps = poolStatsByAddr.get(p.pool_address) ?? ({} as Partial<PoolStatsRow>)
        const onchainVolumeUsd = Number(ps.damm_v2_cumulative_volume_usd_onchain ?? 0)
        const dbVolumeSol = Number(dbVolumesByPool.get(p.damm_pool_address!) ?? 0)
        const dbVolumeUsd = dbVolumeSol * solPriceUsd
        const captureRate = onchainVolumeUsd > 0 ? dbVolumeUsd / onchainVolumeUsd : 0
        let status: 'ok' | 'warning' | 'error' = 'ok'
        if (onchainVolumeUsd > 100) {
          if (captureRate < 0.80) status = 'error'
          else if (captureRate < 0.95) status = 'warning'
        }
        return {
          symbol: p.symbol,
          mintAddress: p.mint_address,
          dammPoolAddress: p.damm_pool_address!,
          onchainVolumeUsd,
          dbVolumeSol,
          dbVolumeUsd,
          captureRate,
          status,
          syncedAt: ps.damm_v2_synced_at ?? null,
          baseDecimals: ps.damm_v2_base_decimals ?? null,
          totalLpBFeeLamports: Number(ps.damm_v2_creator_fee_b_side_lamports ?? 0),
        }
      })

      // Sentry alert: any pool with capture rate < 80% AND non-trivial volume = drift.
      // Tags follow feedback_sentry_capture_required_tags.
      for (const row of perPool) {
        if (row.status === 'error') {
          Sentry.withScope((scope) => {
            scope.setTag('feature', 'global-metrics')
            scope.setTag('rpc_provider', 'triton')
            scope.setTag('environment', import.meta.env.MODE === 'production' ? 'production' : 'preview')
            scope.setTag('pool_address', row.dammPoolAddress)
            scope.setLevel('error')
            Sentry.captureMessage(
              `Reconciliation drift: ${row.symbol} captureRate=${(row.captureRate * 100).toFixed(1)}% (db $${row.dbVolumeUsd.toFixed(0)} vs on-chain $${row.onchainVolumeUsd.toFixed(0)})`
            )
          })
        }
      }

      return { stats, perPool, solPriceUsd, fetchedAt: Date.now() }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  })
}
